import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Clock, Mail, Phone, User, Calendar, ArrowLeft, Heart, Church, Facebook, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import api from "@/lib/api";

export default function CommunityDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const [c, setC] = useState(null);

  useEffect(() => { api.get(`/communities/${id}`).then((r) => setC(r.data)).catch(() => {}); }, [id]);

  if (!c) return <div className="max-w-4xl mx-auto px-4 py-20 text-slate-500">{t("loading")}</div>;

  return (
    <div>
      <section className="bg-th-blue text-white py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/comunidades" className="inline-flex items-center gap-1 text-white/70 hover:text-th-gold text-sm mb-6" data-testid="back-communities"><ArrowLeft className="w-4 h-4" /> {t("nav_communities")}</Link>
          <div className="text-xs uppercase tracking-widest text-th-gold font-semibold">{c.country} · {c.diocese}</div>
          <h1 className="mt-2 font-display font-bold text-4xl sm:text-5xl">{c.name}</h1>
          <p className="mt-4 text-white/75 max-w-2xl">{c.description}</p>
          <Link to={`/aderir?community=${c.id}`}>
            <Button className="mt-6 bg-th-gold text-th-bluedark hover:bg-th-goldlight rounded-full px-7" data-testid="community-participate-btn">
              <Heart className="w-4 h-4 mr-2" />{t("participate")}
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-2 gap-10">
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm space-y-4">
          <h2 className="font-display font-semibold text-xl text-th-blue mb-2">{t("contact")}</h2>
          {[
            { icon: MapPin, label: t("parish"), value: `${c.parish}${c.address ? " · " + c.address : ""}` },
            { icon: Church, label: "Pároco", value: c.parish_priest },
            { icon: Clock, label: t("schedule"), value: [c.periodicity, c.prayer_day, c.prayer_time].filter(Boolean).join(" · ") || c.schedule || null },
            { icon: User, label: t("coordinator"), value: c.coordinator_name },
            { icon: Mail, label: "Email", value: c.contact_email },
            { icon: Phone, label: t("contact"), value: c.contact_phone },
          ].filter((x) => x.value).map((x, i) => (
            <div key={i} className="flex items-start gap-3">
              <x.icon className="w-5 h-5 text-th-gold mt-0.5" />
              <div><div className="text-xs uppercase tracking-wide text-slate-400">{x.label}</div><div className="text-slate-700">{x.value}</div></div>
            </div>
          ))}
          {(c.facebook || c.instagram) && (
            <div className="flex items-center gap-3 pt-2">
              {c.facebook && <a href={c.facebook} target="_blank" rel="noreferrer" data-testid="community-facebook" className="text-th-blue hover:text-th-gold"><Facebook className="w-5 h-5" /></a>}
              {c.instagram && <a href={c.instagram} target="_blank" rel="noreferrer" data-testid="community-instagram" className="text-th-blue hover:text-th-gold"><Instagram className="w-5 h-5" /></a>}
            </div>
          )}
        </div>

        <div>
          <h2 className="font-display font-semibold text-xl text-th-blue mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-th-gold" />{t("upcoming_events")}</h2>
          <div className="space-y-3" data-testid="community-events">
            {(c.upcoming_events || []).length === 0 && <p className="text-slate-500 text-sm">{t("no_events")}</p>}
            {(c.upcoming_events || []).map((e) => (
              <Link to={`/eventos/${e.id}`} key={e.id} data-testid={`community-event-${e.id}`} className="block bg-white rounded-xl p-4 border border-slate-200 hover:border-th-gold transition-colors">
                <div className="text-xs text-th-gold font-semibold">{new Date(e.date).toLocaleDateString()}</div>
                <div className="font-medium text-th-blue">{e.title}</div>
                <div className="text-sm text-slate-500">{e.location}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
